import { useEffect, useRef } from 'react';

export function executeLater(execute: () => void, delay: number): Promise<void> {
  return new Promise(function (resolve) {
    setTimeout(() => {
      execute();
      resolve();
    }, delay);
  });
}

export function wait(seconds: number): Promise<void> {
  return new Promise((res) => {
    setTimeout(res, seconds * 1000);
  });
}

export function debouncer(time: number): (func: () => void) => void {
  const lastInvocation: { current: Date | null } = { current: null }; // make sure the most recent value is always as a ref
  const nextFunc: { current: (() => void) | null } = { current: null };
  const counter = { current: 0 };
  function isReady(): boolean {
    return lastInvocation.current === null || (new Date().getTime() - lastInvocation.current.getTime()) * 0.001 >= time;
  }
  function run(): void {
    if (!nextFunc.current) return;
    lastInvocation.current = new Date();
    nextFunc.current();
  }
  return function (func) {
    counter.current++;
    nextFunc.current = func;
    if (isReady()) {
      run();
    } else {
      wait(time).then(() => {
        if (isReady()) {
          run();
        }
      });
    }
  };
}

export function useDebounce(time: number): ReturnType<typeof debouncer> {
  return useRef(debouncer(time)).current;
}

export function allPromisesSettled<T>(promises: Promise<T>[]): Promise<Array<{ fulfilled: boolean; value: T }>> {
  return Promise.all(
    promises.map((promise) =>
      promise
        .then((value) => ({
          fulfilled: true,
          value,
        }))
        .catch((value) => ({
          fulfilled: false,
          value,
        }))
    )
  );
}

export async function loadAll<T>(
  load: (page: number) => Promise<{ data: T[]; page: number; totalPages: number }>
): Promise<T[]> {
  const results: T[] = [];
  async function poll(page: number): Promise<void> {
    const result = await load(page);
    result.data.forEach((e) => results.push(e));
    if (result.page <= result.totalPages) {
      await poll(page + 1);
    }
  }
  await poll(1);
  return results;
}

export function delay(time: number): Promise<void> {
  if (time > 0) {
    return new Promise((res) => {
      setTimeout(res, time * 1000);
    });
  } else {
    return Promise.resolve();
  }
}

export function useInterval(seconds: number, exec: () => Promise<void>): void {
  let ref: NodeJS.Timeout | undefined = undefined;
  const func = useRef(exec);
  func.current = exec;
  function schedule(): void {
    ref = setTimeout(async () => {
      try {
        await func.current();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
      schedule();
    }, seconds * 1000);
  }
  useEffect(() => {
    schedule();
    return () => ref && clearTimeout(ref);
  }, []);
}
